import { getAnalytics } from "@tm/insurance/analytics";
import { downloadPdfPost } from "@tm/insurance/utils";
import { useEffect, useMemo, useState } from "react";
import FormattedText from "../../../FormattedText/FormattedText";
import LandingButton from "../../../LandingButton/LandingButton";
import type { DownloadBenefitsBottomsheetData } from "../../ListingPageV2.types";
import {
  Body,
  CloseBtn,
  Description,
  Footer,
  Overlay,
  RadioGroup,
  RadioInner,
  RadioLabel,
  RadioOuter,
  RadioRow,
  Sheet,
  SheetHeader,
  SheetTitle,
} from "./Styles";

function defaultSelectionIndex(
  list: NonNullable<DownloadBenefitsBottomsheetData["benefitsDownloadList"]>
) {
  const idx = list.findIndex((item) => /without/i.test(item.label || ""));
  return idx >= 0 ? idx : 0;
}

export interface DownloadBenefitsBottomsheetProps {
  open: boolean;
  onClose: () => void;
  data: DownloadBenefitsBottomsheetData;
  quoteId?: string;
  csrfToken?: string;
  onDownload?: (payload: { url: string; label?: string }) => void;
}

export const DownloadBenefitsBottomsheet = ({
  open,
  onClose,
  data,
  quoteId,
  csrfToken,
  onDownload,
}: DownloadBenefitsBottomsheetProps) => {
  const analytics = getAnalytics();
  const list = useMemo(
    () => data.benefitsDownloadList?.filter((x) => x.label) ?? [],
    [data.benefitsDownloadList]
  );

  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!open || !list.length) return;
    setSelectedIndex(defaultSelectionIndex(list));
  }, [open, list]);

  const headerTitle = data.heading?.trim() || "Download Benefits";

  const effectiveIndex = list.length
    ? Math.min(selectedIndex, list.length - 1)
    : 0;
  const selected = list[effectiveIndex];
  const ctaLabel = data.ctaTxt?.trim() || "Download Now";

  if (!open) return null;

  const onConfirm = async () => {
    if (!list.length) return;
    const url = selected?.url?.trim() ?? "";
    onDownload?.({ url, label: selected?.label });
    if (quoteId) {
      const itineraryId =
        new URLSearchParams(window.location.search).get("itineraryId") ||
        sessionStorage.getItem("itineraryId") ||
        "";
      const templateType = selected?.id || "with_price";
      analytics?.trackListingEvents({
        actionType: `policy_downloaded_${
          templateType === "with_price" ? "with_price" : "without_price"
        }`,
      });
      await downloadPdfPost(quoteId, templateType, csrfToken, itineraryId);
    } else if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
    onClose();
  };

  return (
    <Overlay
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Sheet role="dialog" aria-modal aria-labelledby="dl-benefits-title">
        <SheetHeader>
          <CloseBtn type="button" onClick={onClose} aria-label="Close">
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 1L1 13M1 1L13 13"
                stroke="#757575"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </CloseBtn>
          <SheetTitle id="dl-benefits-title">{headerTitle}</SheetTitle>
        </SheetHeader>

        <Body>
          {data.title ? (
            <Description>
              <FormattedText
                value={data.title}
                baseStyle={{
                  fontSize: "14px",
                  fontWeight: 400,
                  color: "#4a4a4a",
                  lineHeight: 1.5,
                }}
              />
            </Description>
          ) : null}

          <RadioGroup role="radiogroup" aria-label="Download options">
            {list.map((item, index) => {
              const sel = index === effectiveIndex;
              return (
                <RadioRow
                  key={`${item.label}-${index}`}
                  type="button"
                  role="radio"
                  aria-checked={sel}
                  $selected={sel}
                  onClick={() => setSelectedIndex(index)}
                >
                  <RadioOuter $selected={sel} aria-hidden>
                    {sel ? <RadioInner /> : null}
                  </RadioOuter>
                  <RadioLabel $selected={sel}>{item.label}</RadioLabel>
                </RadioRow>
              );
            })}
          </RadioGroup>
        </Body>

        <Footer>
          <LandingButton
            customTitle={ctaLabel}
            customWidth="100%"
            disableState={!list.length}
            shadowVariant="none"
            onClick={onConfirm}
          />
        </Footer>
      </Sheet>
    </Overlay>
  );
};
